import React, { useEffect, useState } from 'react';
import { Truck, Factory, Package, ScanLine, CheckCircle } from 'lucide-react';

/**
 * Hero visual da homepage principal: logo QualiManager + banner do fluxo de qualidade.
 * Respeita prefers-reduced-motion (versão estática).
 */
const MarketingHeroLottie = ({ className = '' }) => {
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  return <HeroQualityFlowVisual className={className} animate={!reduceMotion} />;
};

function HeroQualityFlowVisual({ className = '', animate = true }) {
  const steps = [
    { Icon: Truck, label: 'Recepção', sub: 'Ingredientes e lotes' },
    { Icon: Factory, label: 'Produção', sub: 'Ordens e qualidade' },
    { Icon: Package, label: 'Produto pronto', sub: 'Lote de fabricação' },
    { Icon: ScanLine, label: 'Rastreabilidade', sub: 'Do fornecedor ao cliente' }
  ];

  return (
    <div
      className={`mx-auto w-full max-w-2xl ${className}`}
      role="img"
      aria-label="QualiManager: recepção, produção, produto acabado e rastreabilidade com verificações de qualidade"
    >
      <div className="flex justify-center mb-6 md:mb-8">
        <img
          src="/logo-qualimanager.jpeg"
          alt="QualiManager"
          className="h-36 md:h-44 w-auto max-w-[280px] object-contain object-center"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      <div className="relative rounded-2xl border border-blue-100 bg-gradient-to-br from-slate-50 to-blue-50/80 p-6 md:p-8 shadow-inner">
        <div
          className="absolute top-1/2 left-8 right-8 h-0.5 bg-gradient-to-r from-blue-300 via-cyan-400 to-orange-400 -translate-y-1/2 hidden md:block"
          aria-hidden
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 relative z-10">
          {steps.map(({ Icon, label, sub }, i) => (
            <div key={label} className="flex flex-col items-center text-center">
              <div
                className={`relative mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-md ring-2 ring-blue-100 ${
                  animate ? 'animate-pulse' : ''
                }`}
                style={animate ? { animationDelay: `${i * 0.35}s`, animationDuration: '2.8s' } : undefined}
              >
                <Icon className="h-8 w-8 text-blue-600" strokeWidth={1.5} />
                <span
                  className={`absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 text-white shadow ${
                    animate ? 'opacity-100' : 'opacity-90'
                  }`}
                  style={animate ? { animation: `heroCheck 2.8s ease-in-out ${i * 0.35}s infinite` } : undefined}
                >
                  <CheckCircle className="h-4 w-4" aria-hidden />
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{label}</span>
              <span className="text-xs text-gray-500 mt-0.5 leading-tight">{sub}</span>
            </div>
          ))}
        </div>
        <style>{`
          @keyframes heroCheck {
            0%, 40% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.15); opacity: 0.85; }
            60%, 100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default MarketingHeroLottie;
